/**
 * Normalizes a company name into a stable comparison key.
 *
 * The "change company" feature now sources the switchable list from Apollo's
 * `employment_history`, which exposes a company by NAME / `organization_id`
 * only — there are no LinkedIn URLs to compare (unlike the old job_history
 * path). So the active company, the already-scanned `search_companies`, and the
 * dropdown entries all have to be matched on name instead.
 *
 * Names arrive in slightly different shapes across those sources (casing,
 * punctuation, legal suffixes like "Inc"/"LLC", extra whitespace). A raw `===`
 * comparison produces false negatives — wrongly treating an already-scanned
 * company as new, which would consume a credit. This collapses the common
 * variants to one key so the sides match.
 */
const LEGAL_SUFFIXES = /\b(inc|incorporated|llc|l\.l\.c|ltd|limited|corp|corporation|co|company|gmbh|plc|llp|lp|sa|ag|bv|nv|srl|sas|pty|group|holdings?)\b/g;

export function normalizeCompanyName(name: string | null | undefined): string {
  if (!name) {
    return '';
  }

  let n = String(name).trim().toLowerCase();
  n = n.replace(/[.,&/\\()'"-]/g, ' '); // drop punctuation
  n = n.replace(LEGAL_SUFFIXES, ' ');   // drop legal suffixes
  n = n.replace(/\s+/g, ' ').trim();    // collapse whitespace

  return n;
}

/**
 * Returns true only when both names are present and normalize to the same value.
 * An empty / missing name never matches anything — so a company with no name is
 * never mistaken for an already-scanned one (fail safe for credit usage).
 */
export function sameCompanyName(a: string | null | undefined, b: string | null | undefined): boolean {
  const na = normalizeCompanyName(a);
  const nb = normalizeCompanyName(b);
  return na !== '' && na === nb;
}